import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Button, FormHashtagList, FormCategoryList, GenericSelectionModal, Header, Icon, Screen, Text, TextInput, BackButtonWeb } from '@components'
import { useTheme } from '@react-navigation/native'
import { getImportContactsStyles } from './styles'
import { ImportContactsProps } from './types'
import { getCommonStyles, GroupedItem, Scope, SelectionItem } from '@utils';
import { Category, useCreateGhostProfile, useGetProfessionSectors, usePaginateProfessions, useScanBusinessCard, useScreen, useUpdateGhostProfile } from '@hooks';
import { ActivityIndicator, View } from 'react-native'
import { useLanguage } from '@context'
import * as ImagePicker from 'expo-image-picker'
import { ProfileItem } from './components/ProfileItem'
import { TouchableOpacity } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { useForm } from 'react-hook-form'
import { useFocusEffect, useNavigation } from '@react-navigation/native'
import type { ScanBusinessCardResponse } from '../../../hooks/api/mutations/ghostProfile/useScanBusinessCard'
import { usePendingCardScanStore } from './store/usePendingCardScanStore'
import { FileImportSection } from './fileImport/FileImportSection'
import { ConnectedServices } from './components/ConnectedServices'

export const ImportContacts: React.FC<ImportContactsProps> = () => {
  const theme = useTheme()
  const navigation = useNavigation()
  const { isWeb, isMobile, isDesktop } = useScreen()
  const commonStyles = getCommonStyles(theme)
  const styles = getImportContactsStyles(theme)
  const { t } = useLanguage()

  const {control} = useForm()

  const [isCardLoaded, setIsCardLoaded] = useState(false)
  const [isScanning, setIsScanning] = useState(false)
  const [scanError, setScanError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [ghostProfileId, setGhostProfileId] = useState<string | null>(null)
  const [imageUrl, setImageUrl] = useState<string | null>(null)

  const [familyName, setFamilyName] = useState('')
  const [firstName, setFirstName] = useState('')
  const [professions, setProfessions] = useState<SelectionItem[]>([])
  const [company, setCompany] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [address, setAddress] = useState('')
  const [website, setWebsite] = useState('')
  const [hashtags, setHashtags] = useState<string[]>([])
  const [categories, setCategories] = useState<Category[]>([])

  const fileInputRef = useRef<HTMLInputElement>(null)
  const { mutateAsync: scanBusinessCard } = useScanBusinessCard()
  const { mutateAsync: createGhostProfile } = useCreateGhostProfile()
  const { mutate: updateGhostProfile, isPending: isSaving } = useUpdateGhostProfile()

  const [searchText, setSearchText] = useState('')
  const { data: professionSectors = [] } = useGetProfessionSectors()
  const {
    flatData: allProfessions,
    isFetching: isFetchingProfessions,
    isFetchingNextPage: isFetchingNextPageProfessions,
    fetchNextPage: fetchNextPageProfessions,
    hasNextPage: hasNextPageProfessions,
  } = usePaginateProfessions(searchText)

  const profile = ghostProfileId ? {
    id: ghostProfileId,
    accountId: ghostProfileId,
    avatarUrl: imageUrl || 'https://via.placeholder.com/150',
    fullName: `${firstName} ${familyName}`.trim() || 'Contact importé',
    professions: company ? [{ id: '1', name: company, sectorId: '1' }] : [],
    hashtags: [],
    scope: Scope.PRIVATE,
  } : null

  // Group professions by sector
  const groupedProfessions: GroupedItem[] = useMemo(() => {
    if (!professionSectors.length || !allProfessions.length) return []

    return professionSectors.map(sector => ({
      id: sector.id,
      name: sector.name,
      items: allProfessions
        .filter(prof => prof.sectorId === sector.id)
        .map(prof => ({
          id: prof.id,
          name: prof.name,
        })),
    })).filter(group => group.items.length > 0)
  }, [professionSectors, allProfessions])

  const [isPositionModalVisible, setIsPositionModalVisible] = useState(false)

  // Pre-fill the review form with the parsed card
  const applyScanResult = useCallback((result: ScanBusinessCardResponse) => {
    setFirstName(result.parsedData.firstName || '')
    setFamilyName(result.parsedData.lastName || '')
    setEmail(result.parsedData.email || '')
    setPhone(result.parsedData.phone || '')
    setCompany(result.parsedData.company || '')
    setWebsite(result.parsedData.website || '')
    setAddress(result.parsedData.address || '')
    setGhostProfileId(result.ghostProfileId)
    setImageUrl(result.avatarUrl ?? result.imageUrl)
    setIsCardLoaded(true)
  }, [])

  // A scan made from the camera screen (CardScanner) lands here for review.
  useFocusEffect(
    useCallback(() => {
      const scan = usePendingCardScanStore.getState().take()
      if (scan) applyScanResult(scan)
    }, [applyScanResult]),
  )

  const handleFileSelected = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setScanError('Veuillez sélectionner une image (JPEG, PNG)')
      return
    }

    setIsScanning(true)
    setScanError(null)

    try {
      // Convert file to data URL for the mutation
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })

      const result = await scanBusinessCard(dataUrl)

      applyScanResult(result)
    } catch (e: any) {
      console.error('Scan failed:', e)
      setScanError(e?.message || 'Erreur lors du scan de la carte de visite')
    } finally {
      setIsScanning(false)
    }
  }, [scanBusinessCard, applyScanResult])

  const handleNativePick = useCallback(async () => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync()
    if (status !== 'granted') return

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.8,
    })
    if (result.canceled || !result.assets[0]) return

    const uri = result.assets[0].uri
    setIsScanning(true)
    setScanError(null)
    try {
      const scanned = await scanBusinessCard(uri)
      applyScanResult(scanned)
    } catch (e: any) {
      setScanError(e?.message || 'Erreur lors du scan de la carte de visite')
    } finally {
      setIsScanning(false)
    }
  }, [scanBusinessCard, applyScanResult])

  const handleBack = () => {
    setIsCardLoaded(false)
    setScanError(null)
    setGhostProfileId(null)
    setImageUrl(null)
    setFirstName('')
    setFamilyName('')
    setEmail('')
    setPhone('')
    setCompany('')
    setWebsite('')
    setAddress('')
  }

  // Persist the user's corrections, then open the profile. Without this
  // button the edits were silently lost — users pressed Back and thought
  // the scan was broken.
  const handleValidate = () => {
    if (!ghostProfileId) return
    updateGhostProfile(
      {
        id: ghostProfileId,
        firstName,
        lastName: familyName,
        company,
        residence: address,
        professionIds: professions.map((p) => p.id),
        hashtags,
        pinsCategories: categories.map((c) => c.id),
        contacts: { email, phone, website },
      },
      { onSuccess: () => handleViewProfile() },
    )
  }

  const handleViewProfile = () => {
    if (!ghostProfileId) return
    ;(navigation as any).navigate('Profile', {
      screen: 'DetailsGhostProfile',
      params: { profileId: ghostProfileId },
    })
  }

  return (
    <Screen>
      {isCardLoaded && <BackButtonWeb title={t('common.back')} onPress={handleBack}/>}
      <Header
        title={t('menuItems.importContacts')}
        iconLeft={isWeb? undefined : "arrowBack"}
        iconColor={theme.colors.secondary}
      />
      <ScrollView style={commonStyles.flex}>
        {!isCardLoaded &&
          <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', paddingHorizontal: theme.spacing.xl, paddingVertical: theme.spacing.xl }}>
            <View style={[{
              flexDirection: isMobile ? 'column' : 'row',
              width: '100%',
              maxWidth: 860,
            } as any]}>
              {/* Card upload — left on desktop, top on mobile */}
              <View style={{ flex: isMobile ? undefined : 1, gap: theme.spacing.sm }}>
                <Text type='bodySmall' color={theme.colors.lightGrey}>{t('importContacts.businessCard')}</Text>
                {isWeb ? (
                  <>
                    <input
                      ref={fileInputRef as any}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e: any) => {
                        const file = e.target.files?.[0]
                        if (file) handleFileSelected(file)
                        e.target.value = ''
                      }}
                    />
                    <div
                      onDragOver={(e: any) => { e.preventDefault(); setIsDragging(true) }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={(e: any) => {
                        e.preventDefault()
                        setIsDragging(false)
                        const file = e.dataTransfer.files?.[0]
                        if (file) handleFileSelected(file)
                      }}
                      style={{
                        borderRadius: 16,
                        borderWidth: 1,
                        borderStyle: 'dashed' as const,
                        borderColor: (theme.colors as any).primary,
                        padding: 48,
                        display: 'flex',
                        flexDirection: 'column' as const,
                        alignItems: 'center',
                        gap: 12,
                        backgroundColor: isDragging ? 'rgba(0,0,0,0.03)' : 'transparent',
                        cursor: isScanning ? 'wait' : 'pointer',
                        transition: 'all 0.2s',
                        opacity: isScanning ? 0.5 : 1,
                        pointerEvents: isScanning ? 'none' as const : 'auto' as const,
                      }}
                      onClick={() => !isScanning && fileInputRef.current?.click()}
                    >
                      {isScanning ? (
                        <ActivityIndicator size="large" color={theme.colors.primary} />
                      ) : (
                        <>
                          <Icon name='uploadCloud' size={100} color={theme.colors.lightGrey}/>
                          <Text type='body' color={theme.colors.lightGrey}>{t('importContacts.dragCard')}</Text>
                          <Text type='body' color={theme.colors.lightGrey}>{t('importContacts.or')}</Text>
                          <Button title={t('importContacts.selectFiles')} unfilled isSmall onPress={() => fileInputRef.current?.click()}/>
                          <Text type='body' color={theme.colors.lightGrey}>{t('importContacts.supportedFormats')}</Text>
                        </>
                      )}
                    </div>
                  </>
                ) : (
                  <TouchableOpacity
                    onPress={handleNativePick}
                    disabled={isScanning}
                    style={{
                      borderRadius: 16,
                      borderWidth: 1,
                      borderStyle: 'dashed',
                      borderColor: theme.colors.primary,
                      paddingVertical: theme.spacing.xxxl,
                      paddingHorizontal: theme.spacing.xl,
                      alignItems: 'center',
                      gap: theme.spacing.sm,
                      opacity: isScanning ? 0.5 : 1,
                    }}
                  >
                    {isScanning ? (
                      <ActivityIndicator size="large" color={theme.colors.primary} />
                    ) : (
                      <>
                        <Icon name='uploadCloud' size={100} color={theme.colors.lightGrey}/>
                        <Text type='body' color={theme.colors.lightGrey}>{t('importContacts.selectFiles')}</Text>
                        <Text type='body' color={theme.colors.lightGrey}>{t('importContacts.supportedFormats')}</Text>
                      </>
                    )}
                  </TouchableOpacity>
                )}
                {scanError && (
                  <Text type='bodySmall' color={theme.colors.error}>{scanError}</Text>
                )}
              </View>

              {/* Divider */}
              {isMobile ? (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing.md, marginVertical: theme.spacing.lg }}>
                  <View style={{ flex: 1, height: 1, backgroundColor: theme.colors.grey }} />
                  <Text type='bodySmall' color={theme.colors.lightGrey}>{t('importContacts.or')}</Text>
                  <View style={{ flex: 1, height: 1, backgroundColor: theme.colors.grey }} />
                </View>
              ) : (
                <View style={{ alignItems: 'center', marginHorizontal: theme.spacing.xl }}>
                  <View style={{ flex: 1, width: 1, backgroundColor: theme.colors.grey }} />
                  <View style={{ paddingVertical: theme.spacing.sm }}>
                    <Text type='bodySmall' color={theme.colors.lightGrey}>{t('importContacts.or')}</Text>
                  </View>
                  <View style={{ flex: 1, width: 1, backgroundColor: theme.colors.grey }} />
                </View>
              )}

              {/* Connected services (Unipile contact import) — right on desktop, bottom on mobile */}
              <View style={{ flex: 1, justifyContent: 'center' }}>
                <ConnectedServices />
              </View>
            </View>
            {isWeb && <FileImportSection />}
          </View>}
        {isCardLoaded &&
          <ScrollView style={commonStyles.paddingHorizontal}>
            {profile && (
              <>
                <Text type='bodySmallBold'>{t('importContacts.contactFound')}</Text>
                <View style={styles.card}>
                  <Text type='bodySmall' color={theme.colors.primary}>{t('importContacts.profileFound')}</Text>
                  <ProfileItem profile={profile}/>
                </View>
              </>
            )}
            <View style={commonStyles.gapSm}>
              <Text type='bodySmallBold'>{t('importContacts.addNewContact')}</Text>
              <Text type='bodySmall'>{t('importContacts.checkInfo')}</Text>
            </View>
            <View style={styles.container}>
              <View style={isDesktop && [commonStyles.rowGapLg, commonStyles.alignStart]}>
                <View style={isDesktop && [styles.fixedWidth, commonStyles.paddingRight]}>
                  <TextInput
                    title={t('importContacts.firstName')}
                    value={firstName}
                    onChangeText={setFirstName}
                    placeholder={t('importContacts.enterFirstName')}
                    containerStyle={commonStyles.paddingBottom}
                    style={styles.input}
                    iconRight="userPlus"
                  />

                  <TextInput
                    title={t('importContacts.lastName')}
                    value={familyName}
                    onChangeText={setFamilyName}
                    placeholder={t('importContacts.enterLastName')}
                    containerStyle={commonStyles.paddingBottom}
                    style={styles.input}
                    iconRight="userPlus"
                  />

                  <TouchableOpacity onPress={() => setIsPositionModalVisible(true)}>
                    <TextInput
                      title={t('importContacts.profession')}
                      value={''}
                      placeholder={t('importContacts.enterProfession')}
                      containerStyle={commonStyles.paddingBottom}
                      style={styles.input}
                      iconRight="work"
                      editable={false}
                      pointerEvents="none"
                    />
                  </TouchableOpacity>
                  <TextInput
                    title={t('importContacts.company')}
                    value={company}
                    onChangeText={setCompany}
                    placeholder={t('importContacts.enterCompany')}
                    containerStyle={commonStyles.paddingBottom}
                    style={styles.input}
                    iconRight="work"
                  />
                </View>
                <View style={isDesktop && [styles.fixedWidth, commonStyles.paddingRight]}>
                  <TextInput
                    title={t('importContacts.email')}
                    value={email}
                    onChangeText={setEmail}
                    placeholder={t('importContacts.email')}
                    containerStyle={commonStyles.paddingBottom}
                    style={styles.input}
                    keyboardType="email-address"
                    iconRight="message"
                  />

                  <TextInput
                    title={t('importContacts.phone')}
                    value={phone}
                    onChangeText={setPhone}
                    placeholder={t('importContacts.phone')}
                    containerStyle={commonStyles.paddingBottom}
                    style={styles.input}
                    keyboardType="phone-pad"
                    iconRight="phone"
                  />
                  <TextInput
                    title={t('importContacts.address')}
                    value={address}
                    onChangeText={setAddress}
                    placeholder={t('importContacts.address')}
                    containerStyle={commonStyles.paddingBottom}
                    style={styles.input}
                    keyboardType="url"
                    iconRight="mapPin"
                  />
                  <TextInput
                    title={t('importContacts.website')}
                    value={website}
                    onChangeText={setWebsite}
                    placeholder={t('importContacts.website')}
                    containerStyle={commonStyles.paddingBottom}
                    style={styles.input}
                    keyboardType="url"
                    iconRight="globe"
                  />
                </View>
                <View style={isDesktop && styles.fixedWidth}>
                  <FormHashtagList
                    control={control}
                    name={t('importContacts.hashtag')}
                    placeholder={t('importContacts.hashtagPlaceholder')}
                    values={hashtags}
                    isGhost
                    iconRight="search"
                    addItem={setHashtags}
                    removeItem={setHashtags}
                    style={styles.input}
                    withAutocomplete
                  />
                  <FormCategoryList
                    control={control}
                    name={t('importContacts.categoriesLabel')}
                    placeholder={t('importContacts.categoriesLabel')}
                    values={categories}
                    isGhost
                    iconRight="search"
                    addItem={setCategories}
                    removeItem={setCategories}
                    style={styles.input}
                    withAutocomplete
                  />
                </View>
              </View>
            </View>
            <View style={[commonStyles.alignStart, commonStyles.paddingBottom]}>
              <Button
                title={isSaving ? t('importContacts.validating') : t('importContacts.validate')}
                onPress={handleValidate}
                disabled={isSaving}
              />
            </View>
          </ScrollView>
        }
      </ScrollView>
      <GenericSelectionModal
        isVisible={isPositionModalVisible}
        setIsVisible={setIsPositionModalVisible}
        title={t('importContacts.profession')}
        placeholder={t('importContacts.searchProfession')}
        availableItems={groupedProfessions}
        selectedItems={professions || []}
        multiSelect={true}
        isFetching={isFetchingProfessions}
        isFetchingNextPage={isFetchingNextPageProfessions}
        hasNextPage={hasNextPageProfessions}
        fetchNextPage={fetchNextPageProfessions}
        onSearch={(search) => {
          setSearchText(search)
        }}
        onSelectionChange={(items) => {
          setProfessions(items)
        }}
        fitParent={isMobile}
      />
    </Screen>
  )
}
